import React, { useEffect, useRef, useState } from 'react';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // Update active section based on scroll position
      const sections = ['hero', 'problem', 'solution', 'demo', 'pricing', 'faq'];
      const scrollPosition = window.scrollY + 100;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (sectionId === 'hero') {
      // Scroll all the way to top for About/Hero
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 60; // 60px offset for other sections
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navLinks = [
    { id: 'about', label: 'About', section: 'hero' },
    { id: 'problem', label: 'Problem', section: 'problem' },
    { id: 'pricing', label: 'Pricing', section: 'pricing' }
  ];

  return (
    <nav 
      ref={navRef}
      className={`fixed top-0 left-0 right-0 z-50 bg-white bg-opacity-95 backdrop-blur-lg border-b border-gray-100 transition-all duration-300`}
    >
      <div className="max-w-7xl mx-auto px-8 flex items-center justify-between h-20">
        <div className="flex items-center gap-3 cursor-pointer hover:scale-105 transition-transform" onClick={scrollToTop}>
          <div className="w-8 h-8 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-green-500 transition-colors hover:text-green-600">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="text-xl font-bold text-slate-900 font-sans">CallSafe Training</span>
        </div>

        <div className="hidden md:flex items-center gap-6">
          <ul className="flex items-center gap-8 list-none">
            {navLinks.map((link) => (
              <li key={link.id}>
                <button
                  className={`text-base font-medium transition-colors hover:text-green-500 font-sans ${activeSection === link.section ? 'text-green-500 relative' : 'text-slate-600'}`}
                  onClick={() => scrollToSection(link.section)}
                >
                  {link.label}
                  {activeSection === link.section && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500 rounded"></span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          
          <button 
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 font-sans"
            onClick={() => scrollToSection('pricing')}
          >
            Get Protected Today
          </button>
        </div>

        <button 
          className="md:hidden bg-none border-none cursor-pointer p-2 rounded-lg hover:bg-gray-100 transition-colors"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <div className="w-6 h-5 relative flex flex-col justify-between">
            <span className={`block w-full h-0.5 bg-slate-900 rounded transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2.5' : ''}`}></span>
            <span className={`block w-full h-0.5 bg-slate-900 rounded transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-full h-0.5 bg-slate-900 rounded transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2.5' : ''}`}></span>
          </div>
        </button>
      </div>

      {/* Mobile menu overlay */}
      <div className={`md:hidden fixed top-20 left-0 right-0 bg-white border-b border-gray-100 transform transition-all duration-300 ${isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}`}>
        <div className="px-8 py-8">
          <ul className="flex flex-col gap-4 list-none mb-8">
            {navLinks.map((link) => (
              <li key={link.id}>
                <button
                  className={`text-lg font-medium text-left w-full p-4 rounded-lg transition-colors font-sans ${activeSection === link.section ? 'text-green-500 bg-green-50' : 'text-slate-600 hover:text-green-500 hover:bg-green-50'}`}
                  onClick={() => scrollToSection(link.section)}
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>
          
          <button 
            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 font-sans text-lg"
            onClick={() => scrollToSection('pricing')}
          >
            Get Protected Today
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
